import { HttpErrorResponse } from '@angular/common/http';
import { TimeoutError } from 'rxjs';
import { isUploadRejectionStatus } from './upload-rejection-reason';

export const TRANSPORT_SLOTS = 6;
export const MAX_ATTEMPTS = 4;
export const BACKOFF_BASE_MS = 2000;
export const BACKOFF_MAX_MS = 30_000;
/** Random spread on top of each backoff so parallel slots don't re-hit a recovering proxy in lockstep. */
export const BACKOFF_JITTER_MS = 1000;
/** A silent connection (no event this long) is a hung socket - abort and go transient. */
export const ATTEMPT_IDLE_TIMEOUT_MS = 120_000;
/** Errors are instant with the link down, so a short cheap loop resumes fast. */
export const OFFLINE_PROBE_MS = 5000;
/** One blip that fails every busy slot costs TRANSPORT_SLOTS failures; the threshold sits above one round and below two. */
export const CIRCUIT_THRESHOLD = TRANSPORT_SLOTS * 2 - 2;
export const CIRCUIT_PAUSE_MS = 30_000;
export const CIRCUIT_PAUSE_MAX_MS = 300_000;

export interface TransportErrorInfo {
  readonly status: number;
  readonly timedOut: boolean;
  readonly transient: boolean;
  readonly refusal: HttpErrorResponse | null;
  readonly kind: 'refused' | 'server' | 'transport';
}

export function classifyTransportError(error: unknown): TransportErrorInfo {
  const timedOut = error instanceof TimeoutError;
  const status = error instanceof HttpErrorResponse ? error.status : 0;
  const refusal = error instanceof HttpErrorResponse && isUploadRejectionStatus(error.status) ? error : null;
  return {
    status,
    timedOut,
    transient: timedOut || status === 0 || status === 408 || status === 429 || status >= 500,
    refusal,
    kind: refusal ? 'refused' : status >= 500 ? 'server' : 'transport',
  };
}

export function backoffMs(attempts: number, random: () => number = Math.random): number {
  return Math.min(BACKOFF_BASE_MS * 2 ** attempts, BACKOFF_MAX_MS) + Math.floor(random() * BACKOFF_JITTER_MS);
}
